import React from 'react';
import CloseIcon from 'svg/CloseIcon';
import { ButtonText } from 'components/Button';
import { convertUnitPriceToFormattedPrice } from 'utils/shared/money/convertUnitPriceToFormattedPrice';
import { formatVenueEventDateTime } from 'utils/shared/time/formatEventDateTime';
import classNames from 'styles/utils/classNames';
import { ClinicType } from '../props';
import { resolveVenueTimeZone } from 'utils/shared/time/venueDayFilters';

interface VenueClinicsPopupProps {
  venueId: string;
  venueName: string;
  clinics: ClinicType[];
  onClose: () => void;
  onClinicClick: (clinic: ClinicType) => void;
}

const VenueClinicsPopup: React.FC<VenueClinicsPopupProps> = ({
  venueName,
  clinics,
  onClose,
  onClinicClick,
}) => {
  // Prevent map interactions when interacting with the popup
  const handleMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    e.stopPropagation();
  };

  const handleWheel = (e: React.WheelEvent) => {
    e.stopPropagation();
  };

  return (
    <div
      className="absolute left-7 top-1/2 isolate z-60 ml-4 flex w-[20rem] -translate-y-1/2 transform flex-col rounded-xl bg-color-bg-lightmode-primary p-ds-lg shadow-lg dark:bg-color-bg-darkmode-primary"
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      onWheel={handleWheel}
    >
      <div className="absolute right-4 top-4 z-10 flex items-center justify-center rounded-full bg-color-bg-lightmode-invert bg-opacity-50">
        <ButtonText
          onClick={onClose}
          className="flex items-center justify-center rounded-full p-2"
        >
          <CloseIcon className="h-6 w-6 text-color-text-lightmode-invert" />
        </ButtonText>
      </div>

      <div className="mb-4 pr-8">
        <h3 className="typography-product-subheading mb-1">{venueName}</h3>
        <p className="typography-product-caption text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
          {clinics.length} {clinics.length === 1 ? 'clinic' : 'clinics'} at this venue
        </p>
      </div>

      <div
        className="max-h-[20rem] space-y-2 overflow-y-auto"
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onWheel={handleWheel}
      >
        {clinics.map((clinic) => {
          const clinicDateTime = formatVenueEventDateTime(
            clinic?.startDateTime,
            clinic?.endDateTime,
            resolveVenueTimeZone(clinic),
          );
          const lessonDate = clinicDateTime?.date ?? '';
          const lessonStartTime = clinicDateTime?.time ?? '';
          const price = clinic.priceUnitAmount
            ? convertUnitPriceToFormattedPrice(clinic.priceUnitAmount, clinic.currency).priceDisplay
            : null;

          return (
            <button
              key={clinic.id}
              onClick={() => onClinicClick(clinic)}
              className={classNames(
                'w-full rounded-lg border p-3 text-left transition-colors',
                'border-color-border-card-lightmode dark:border-color-border-card-lightmode',
                'bg-color-bg-lightmode-primary hover:bg-color-bg-lightmode-secondary',
                'dark:bg-color-bg-darkmode-primary dark:hover:bg-color-bg-darkmode-secondary',
              )}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <h4 className="typography-product-card-title-desktop truncate">{clinic.title}</h4>
                  <div className="mt-1 flex flex-col gap-1">
                    <span className="typography-product-caption text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                      {lessonDate} • {lessonStartTime}
                      {clinicDateTime?.timezoneLabel ? ` ${clinicDateTime.timezoneLabel}` : ''}
                    </span>
                    {clinic.ownerProfile?.fullName && (
                      <span className="typography-product-caption text-color-text-lightmode-tertiary dark:text-color-text-darkmode-tertiary">
                        {clinic.ownerProfile.fullName}
                      </span>
                    )}
                  </div>
                </div>
                {price && (
                  <span className="typography-product-subheading shrink-0">{price}</span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default VenueClinicsPopup;
